import { useEffect, useRef, useState } from "react";
import logoLockup from "../brand/assets/logo-lockup-trans.png";
import markGradient from "../brand/assets/mark-gradient.png";
import { useEnvironment } from "../lib/environment";

// Pre-launch holding page shown to signed-out visitors before the app opens up.
// Ported from the design handover (a self-contained static page) into the SPA so
// it shares the Mockingbird tokens/fonts and can POST waitlist signups to our own
// backend. Styles live under the `.mb-holding` scope in styles.css.

const HEADLINES = [
  <>The production desk for <span className="g">audiobooks.</span></>,
  <>Proof audiobooks <span className="g">against the&nbsp;manuscript.</span></>,
  <>Check audio against <span className="g">required&nbsp;specs.</span></>,
];

const FEATURES = [
  {
    title: "Listen against the script",
    body: "Mockingbird checks the recording for completeness and adherence to the manuscript — flagging drops, adds and mispronunciations.",
    icon: (
      <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" />
    ),
  },
  {
    title: "Generate pickups",
    body: "Turn every flag into a clean, share-ready pickup list — ready for the booth and the editor.",
    icon: (
      <>
        <path d="M3 7V5a2 2 0 0 1 2-2h2" />
        <path d="M17 3h2a2 2 0 0 1 2 2v2" />
        <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
        <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
        <path d="M7 8h8" />
        <path d="M7 12h10" />
        <path d="M7 16h6" />
      </>
    ),
  },
  {
    title: "Validate before delivery",
    body: "Check technical specs against each publishing platform's requirements, so files pass QC the first time.",
    icon: (
      <path d="M2 13a2 2 0 0 0 2-2V7a2 2 0 0 1 4 0v13a2 2 0 0 0 4 0V4a2 2 0 0 1 4 0v13a2 2 0 0 0 4 0v-4a2 2 0 0 1 2-2" />
    ),
  },
];

type State = "idle" | "sending" | "done" | "error";

export function HoldingPage({ onSignIn }: { onSignIn?: () => void }) {
  const [active, setActive] = useState(0);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState("");
  const submitted = useRef("");
  const env = useEnvironment();   // the staging copy says so here too (and in the tab title)

  // Rotate the headline through its three lines, crossfading every 2.2s. All
  // lines stack in one grid cell so the layout below never shifts.
  useEffect(() => {
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % HEADLINES.length),
      2200
    );
    return () => window.clearInterval(id);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value || state === "sending") return;
    setState("sending");
    setError("");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || "Something went wrong. Please try again.");
      }
      submitted.current = value;
      setState("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setState("error");
    }
  }

  return (
    <div className="mb-holding">
      <div className="page">
        <div className="topbar">
          <img className="mini" src={markGradient} alt="Mockingbird Audio" />
          <div className="top-right">
            {env !== "production" && <div className="top-pill env-badge">{env} · test copy</div>}
            <div className="top-pill">Founding access · 2026</div>
            {onSignIn && (
              <button type="button" className="top-signin" onClick={onSignIn}>
                Sign in
              </button>
            )}
          </div>
        </div>

        <main className="hero">
          <img className="lockup" src={logoLockup} alt="Mockingbird Audio" />

          <div className="eq" aria-hidden="true">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} />
            ))}
          </div>

          <h1>
            {HEADLINES.map((line, i) => (
              <span key={i} className={`rot${i === active ? " is-active" : ""}`}>
                {line}
              </span>
            ))}
          </h1>
          <p className="lede">
            Mockingbird is the production desk for audiobook teams — narrators, engineers,
            proofers and directors. Check recorded audio against the manuscript, generate
            pickups in a click, and validate specs before you deliver.
          </p>

          <div className="signup">
            {state !== "done" ? (
              <>
                <form onSubmit={submit} noValidate>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@studio.com"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (state === "error") setState("idle");
                    }}
                  />
                  <button type="submit" disabled={state === "sending"}>
                    {state === "sending" ? "Adding you…" : "Request early access"}
                  </button>
                </form>
                {error ? (
                  <p className="formerr">{error}</p>
                ) : (
                  <p className="microcopy">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    Founding members get first access &amp; founding pricing. No spam, ever.
                  </p>
                )}
              </>
            ) : (
              <div className="success">
                <div className="check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <h2>You're on the list.</h2>
                <p>
                  We'll reach out to <b>{submitted.current}</b> when your founding spot opens.
                </p>
              </div>
            )}
          </div>
        </main>

        <section className="how">
          <div className="how-head">
            <div className="ovl">How Mockingbird works</div>
            <h3>From raw recording to confident delivery</h3>
          </div>
          <div className="grid">
            {FEATURES.map((f) => (
              <div className="feat" key={f.title}>
                <div className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                    {f.icon}
                  </svg>
                </div>
                <h4>{f.title}</h4>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <footer className="foot">© 2026 Mockingbird Audio · mockingbirdaudio.com</footer>
      </div>
    </div>
  );
}
